import { Icon } from '@buggy/shared'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  type FormProps,
} from 'antd'
import { compact } from 'lodash-es'
import { useState, type FunctionComponent } from 'react'
import styled from 'styled-components'
import { z } from 'zod/v4'
import CollaborativeItemFormList from '@/components/common/collaborative/collaborative-item-form-list'
import { type CollaborativeItemTypeCascaderProps } from '@/components/common/collaborative/collaborative-item-type-cascader'
import SelectKolPostModal from '@/components/common/modals/select-kol-post-modal'
import CollaborationRecordPost from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/collaboration-record-post'
import { DEFAULT_DATE_FORMAT } from '@/constants/date-format'
import textMaximumLength from '@/constants/text-maximum-length'
import useFormatPrice from '@/hooks/use-format-price'
import useLocalizedKolQuotationCurrency from '@/hooks/use-localized-kol-quotation-currency'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Nullable } from '@/types/nullable'
import type { Optional } from '@/types/optional'
import {
  draftRecordSchema,
  postSchema,
  type Post,
} from '@/types/schema/directory/collaboration-record'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import { daySchema } from '@/types/schema/zod/day-schema'
import { convertPlatformType } from '@/utils/convert-platform'

const MAXIMUM_POST_COUNT = 20
const MAXIMUM_COLLABORATIVE_ITEM_COUNT = 20

type DisabledOptions = NonNullable<
  CollaborativeItemTypeCascaderProps['disabledOptions']
>

export const collaborationRecordFormDataExtraSchema = z.object({
  duration: z.tuple([daySchema, daySchema]),
  post_list: postSchema.array(),
})
const collaborationRecordFormDataSchema = z.object({
  ...draftRecordSchema.omit({
    start_at: true,
    end_at: true,
    post_list: true,
  }).shape,
  ...collaborationRecordFormDataExtraSchema.shape,
})
export type CollaborationRecordFormData = z.infer<
  typeof collaborationRecordFormDataSchema
>

interface CollaborationRecordFormProps
  extends Omit<FormProps<CollaborationRecordFormData>, 'layout'> {
  kolInfo: KolSchemaModel
}

const CollaborationRecordForm: FunctionComponent<
  CollaborationRecordFormProps
> = ({ kolInfo, form, preserve = false, ...props }) => {
  const { formatMessage } = useIntl()
  const { formatPrice } = useFormatPrice()

  const itemListValue = Form.useWatch(
    collaborationRecordFormDataSchema.keyof().enum.item_list,
    form,
  )
  const postListValue = Form.useWatch(
    collaborationRecordFormDataSchema.keyof().enum.post_list,
    form,
  )

  const [selectPostsModalOpen, setSelectPostsModalOpen] = useState(false)

  const localizedKolQuotationCurrency = useLocalizedKolQuotationCurrency()

  const disabledOptions: Optional<DisabledOptions> = itemListValue
    ?.filter(
      (item) => !item.is_custom_platform && !item.is_custom_collaborative_item,
    )
    .reduce((options: DisabledOptions, item) => {
      const existingOptionsByPlatform = options.find(
        (option) => option.platform === item.platform_name,
      )

      const newOptionsByPlatform = {
        platform: item.platform_name,
        items: existingOptionsByPlatform
          ? [...existingOptionsByPlatform.items, item.collaborative_item]
          : [item.collaborative_item],
      }

      return [
        ...options.filter((option) => option.platform !== item.platform_name),
        newOptionsByPlatform,
      ]
    }, [])
  const totalFee = (itemListValue ?? []).reduce((totalCooperationFee, item) => {
    return totalCooperationFee + item.cooperation_fee
  }, 0)

  return (
    <StyledForm {...props} form={form} layout='vertical' preserve={preserve}>
      <Row gutter={22}>
        <Col span={12}>
          <FormItem
            label={formatMessage({
              id: 'kol_cooperation_detail_drawer:quotation_name',
            })}
            name={collaborationRecordFormDataSchema.keyof().enum.name}
            rules={[
              {
                required: true,
                message: formatMessage(
                  {
                    id: 'error:please_fulfill',
                  },
                  {
                    inputField: formatMessage({
                      id: 'kol_cooperation_detail_drawer:quotation_name',
                    }),
                  },
                ),
              },
              {
                max: textMaximumLength.title,
                message: formatMessage(
                  {
                    id: 'error:input_filed_maximum_characters',
                  },
                  {
                    limit: textMaximumLength.title,
                  },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'kol_cooperation_detail_drawer:quotation_name',
              })}
              size='small'
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            label={formatMessage({ id: 'general:collaboration_duration' })}
            name={collaborationRecordFormDataSchema.keyof().enum.duration}
            rules={[
              {
                type: 'array',
                required: true,
                message: formatMessage(
                  { id: 'error:please_select' },
                  {
                    selectField: formatMessage({
                      id: 'general:collaboration_duration',
                    }),
                  },
                ),
              },
            ]}
          >
            <DatePicker.RangePicker
              format={DEFAULT_DATE_FORMAT}
              placeholder={[
                formatMessage({ id: 'general:startdate_placeholder' }),
                formatMessage({ id: 'general:enddate_placeholder' }),
              ]}
              size='small'
            />
          </FormItem>
        </Col>
      </Row>
      <FormItem
        required
        label={
          <Flex align='center' justify='space-between'>
            {formatMessage({ id: 'campaign:cooperation_title' })}
            <CurrencyLabel>{localizedKolQuotationCurrency}</CurrencyLabel>
          </Flex>
        }
      >
        <CollaborativeItemFormList
          disabledItemTypeCascaderOptions={disabledOptions}
          maxItemCount={MAXIMUM_COLLABORATIVE_ITEM_COUNT}
          name={collaborationRecordFormDataSchema.keyof().enum.item_list}
          totalFee={[
            formatMessage({ id: 'general:total_text' }),
            formatMessage({ id: 'general:colon' }),
            formatPrice(totalFee),
          ].join('')}
        />
      </FormItem>
      <FormItem label={formatMessage({ id: 'autoreport:content_field_link' })}>
        <Form.List
          initialValue={[]}
          name={collaborationRecordFormDataSchema.keyof().enum.post_list}
        >
          {(fields, { remove }) => {
            return (
              <Flex vertical align='flex-start' gap={12}>
                <AddPostButton
                  ghost
                  disabled={fields.length >= MAXIMUM_POST_COUNT}
                  icon={<Icon name='plus' />}
                  size='small'
                  type='link'
                  onClick={() => setSelectPostsModalOpen(true)}
                >
                  {formatMessage({ id: 'general:add_post_act' })}
                </AddPostButton>
                <PostList>
                  {fields.map((field) => {
                    return (
                      <FormItem {...field} key={field.key}>
                        <CollaborationRecordPost
                          kolId={kolInfo.id}
                          kolName={kolInfo.name}
                          onDelete={(): void => {
                            remove(field.name)
                          }}
                        />
                      </FormItem>
                    )
                  })}
                </PostList>
              </Flex>
            )
          }}
        </Form.List>
      </FormItem>
      {selectPostsModalOpen && (
        <SelectKolPostModal
          open
          initialSelectedPostIds={postListValue?.map((post) => post.post_id)}
          kolInfo={kolInfo}
          onCancel={() => setSelectPostsModalOpen(false)}
          onSubmit={(posts) => {
            form?.setFieldsValue({
              post_list: compact(
                posts.map((post): Nullable<Post> => {
                  const postPlatform = convertPlatformType(
                    post.platformType,
                  )?.shortcode

                  if (!postPlatform) {
                    return null
                  }

                  return {
                    id: post.id,
                    url: post.url,
                    platform: postPlatform,
                    post_id: post.postID,
                    post_type: post.postType,
                    title: post.title,
                    content: post.text,
                    like_count: post.likeCount,
                    is_disable_like: post.isDisableLike,
                    comment_count: post.commentCount,
                    share_count: post.shareCount,
                    view_count: post.viewCount,
                    play_count: post.playCount ?? null,
                    thumbnail_url: post.thumbnail,
                    reward: null,
                    crawled_id: post.crawledID,
                    ig_post_id: post.igPostId,
                    reach_count: null,
                    click_count: null,
                    publish_time: post.publishTime,
                  }
                }),
              ),
            })
            setSelectPostsModalOpen(false)
          }}
        />
      )}
    </StyledForm>
  )
}

const StyledForm = styled(Form)`
  max-height: 435px;
  overflow: hidden auto;
`

const FormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    width: 100%;

    &::after {
      display: none;
    }

    .ant-flex {
      width: 100%;
    }
  }

  .ant-picker {
    width: 100%;
  }
`

const CurrencyLabel = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

const AddPostButton = styled(Button)`
  padding: 0;
  height: 24px;
  line-height: 24px;
`

const PostList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;

  .ant-form-item {
    margin: 0;
  }
`

export default CollaborationRecordForm
